// askSmart-ui/src/pages/TOSP/tospApi.ts

// The TOSP backend, one record at a time. Every write carries the ETag the
// record was read with, so a save that would overwrite somebody else's change
// fails loudly (409) instead of silently winning.

const BASE = `${process.env.REACT_APP_BACKEND_URL}/tosp`;

/** Collections exposed by the API, mirroring the backend's route paths. */
export type TospCollection =
  | "buildings"
  | "units"
  | "tenants"
  | "occupancies"
  | "tax-rolls"
  | "tax-years"
  | "budgets";

/** A record as stored, carrying the ETag needed to write it back. */
export type Stored<T> = T & { _etag?: string };

export class TospApiError extends Error {
  status: number;
  /** Another writer got there first — the caller should re-read. */
  conflict: boolean;

  constructor(message: string, status: number) {
    super(message);
    this.name = "TospApiError";
    this.status = status;
    this.conflict = status === 409;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // `...init` must come BEFORE headers: spreading it last let its own
  // `headers` key replace the merged object outright — and on a write with no
  // ETag that key is `undefined`, so Content-Type went missing, Express never
  // parsed the body, and every create came back 400.
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    const detail = await res
      .json()
      .then((body) => (body as { error?: string }).error)
      .catch(() => undefined);
    throw new TospApiError(detail || `Request failed (${res.status})`, res.status);
  }

  return (await res.json()) as T;
}

// ─── Collections ─────────────────────────────────────────────────────────────

export async function listRecords<T>(collection: TospCollection): Promise<Stored<T>[]> {
  const { items } = await request<{ items: Stored<T>[] }>(`/${collection}`);
  return items || [];
}

export async function saveRecord<T extends { id: string }>(
  collection: TospCollection,
  record: Stored<T>,
): Promise<Stored<T>> {
  const { _etag, ...item } = record as Stored<T> & { _etag?: string };
  const { item: saved } = await request<{ item: Stored<T> }>(
    `/${collection}/${encodeURIComponent(record.id)}`,
    {
      method: "PUT",
      headers: _etag ? { "If-Match": _etag } : undefined,
      body: JSON.stringify({ item }),
    },
  );
  return saved;
}

export async function deleteRecord(
  collection: TospCollection,
  id: string,
): Promise<void> {
  await request<void>(`/${collection}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ─── Configuration (one document per org) ────────────────────────────────────

/**
 * Reopens a published budget, recording why.
 *
 * Its own endpoint rather than an ordinary save: the IP on the audit entry has
 * to be the one the server saw, and the reason has to be mandatory somewhere a
 * direct API call can't skip.
 */
export async function unlockBudget<T>(
  id: string,
  reason: string,
  byName: string,
  etag?: string,
): Promise<Stored<T>> {
  const { item } = await request<{ item: Stored<T> }>(`/budgets/${id}/unlock`, {
    method: "POST",
    body: JSON.stringify({ reason, byName }),
    headers: etag ? { "If-Match": etag } : {},
  });
  return item;
}

export async function loadConfiguration<T>(): Promise<Stored<T> | null> {
  const { configuration } = await request<{ configuration: Stored<T> | null }>(
    "/configuration",
  );
  return configuration;
}

export async function saveConfiguration<T>(configuration: Stored<T>): Promise<Stored<T>> {
  const { _etag, ...body } = configuration as Stored<T> & { _etag?: string };
  const { configuration: saved } = await request<{ configuration: Stored<T> }>(
    "/configuration",
    {
      method: "PUT",
      headers: _etag ? { "If-Match": _etag } : undefined,
      body: JSON.stringify({ configuration: body }),
    },
  );
  return saved;
}

// ─── Estoppels ───────────────────────────────────────────────────────────────
//
// Their own routes rather than a collection: certificates carry every
// tenant's rent and deposit, so the backend serves them to admins only.

export interface EstoppelTemplate {
  fileName: string;
  uploadedAt: string;
  uploadedByName?: string;
}

export interface EstoppelCertificate {
  id: string;
  year: number;
  tenantId: string;
  tenantName: string;
  fileName: string;
  generatedAt: string;
  generatedByName?: string;
  /** Put into the tenant's SMRT Docs Estoppel slot, ready for Edit Fields. */
  sentAt?: string;
  sentByName?: string;
  /** Every generation and send, oldest first. */
  history?: { at: string; byName?: string; status: string; fileName?: string }[];
}

export interface EstoppelState {
  template: EstoppelTemplate | null;
  years: number[];
  certificates: EstoppelCertificate[];
}

export const loadEstoppels = (): Promise<EstoppelState> => request<EstoppelState>("/estoppels");

/** Multipart, so it can't go through request(), which sends JSON. */
export async function uploadEstoppelTemplate(file: File, byName: string): Promise<EstoppelTemplate> {
  const form = new FormData();
  form.append("file", file);
  form.append("byName", byName);
  const res = await fetch(`${BASE}/estoppels/template`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new TospApiError(body.error || `Upload failed (${res.status})`, res.status);
  return body.template;
}

export const openEstoppelYear = (year: number): Promise<{ year: number }> =>
  request(`/estoppels/years/${year}`, { method: "PUT", body: "{}" });

export const generateEstoppels = (
  year: number,
  certificates: { tenantId: string; fileName: string; values: unknown }[],
  byName: string,
): Promise<{ certificates: EstoppelCertificate[]; failed: { tenantId: string; error: string }[] }> =>
  request("/estoppels/generate", {
    method: "POST",
    body: JSON.stringify({ year, certificates, byName }),
  });

/** Plain https URLs, opened in a new tab — the browser renders the PDF itself. */
export const estoppelFileUrl = (id: string): string =>
  `${BASE}/estoppels/file/${encodeURIComponent(id)}`;
export const estoppelTemplateUrl = (): string => `${BASE}/estoppels/template/file`;

/** The template as paragraphs and runs, with its markup located — see templateOutline. */
export interface TemplateRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  tab?: boolean;
}

export interface TemplateParagraph {
  index: number;
  align?: "left" | "center" | "right" | "both";
  number?: string;
  level: number;
  runs: TemplateRun[];
  /** [LABEL] fields, as offsets into the paragraph's text. */
  fields: { label: string; start: number; end: number }[];
  /** [[optional]] pieces, as offsets into the paragraph's text. */
  blocks: { start: number; end: number }[];
  /** Brackets that don't pair up, located in this paragraph's text. */
  errors: { message: string; start: number; end: number }[];
}

export interface TemplateOutline {
  paragraphs: TemplateParagraph[];
  labels: { label: string; count: number; suggested: string | null }[];
  blocks: { text: string; labels: string[] }[];
  /** Label → TOSP field key. */
  mapping: Record<string, string>;
  /** The TOSP fields a label can be filled from. */
  fields: { key: string; label: string; group: string }[];
}

export const loadEstoppelTemplateOutline = (): Promise<TemplateOutline> =>
  request<TemplateOutline>("/estoppels/template/outline");

export const saveEstoppelMapping = (mapping: Record<string, string>): Promise<{ mapping: Record<string, string> }> =>
  request("/estoppels/template/mapping", { method: "PUT", body: JSON.stringify({ mapping }) });

/** Fixes one paragraph's text in place. `expected` is the text it was loaded with. */
export const saveEstoppelTemplateParagraph = (index: number, expected: string, text: string): Promise<{ ok: boolean }> =>
  request("/estoppels/template/paragraph", { method: "PUT", body: JSON.stringify({ index, expected, text }) });

/** Puts generated certificates into each tenant's SMRT Docs Estoppel slot. */
export const sendEstoppels = (
  year: number,
  tenantIds: string[],
  byName: string,
): Promise<{ certificates: EstoppelCertificate[]; failed: { tenantId: string; error: string }[] }> =>
  request("/estoppels/send", { method: "POST", body: JSON.stringify({ year, tenantIds, byName }) });

/** Removes a generated certificate and its PDF (not any copy already in SMRT Docs). */
export const deleteEstoppel = (year: number, tenantId: string): Promise<void> =>
  request(`/estoppels/certificates/${year}/${encodeURIComponent(tenantId)}`, { method: "DELETE" });
